import { useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../services/supabase'
import { useToast } from '../contexts/ToastContext'

/**
 * Lightweight session guard
 *
 * - Checks Supabase auth session on mount and whenever the tab becomes visible
 * - If the session is missing, shows a friendly message and navigates to /login
 * - Uses React Router navigation instead of hard page reloads
 * - Includes debouncing and retry logic to prevent false positives on tab switch
 */
export const useSessionGuard = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { error: showError } = useToast()
  const checkTimeoutRef = useRef(null)
  const lastCheckRef = useRef(0)
  const consecutiveFailuresRef = useRef(0)

  useEffect(() => {
    let cancelled = false

    const runCheck = async (isRetry = false) => {
      // Debounce: Don't check too frequently (min 2 seconds between checks)
      const now = Date.now()
      if (!isRetry && now - lastCheckRef.current < 2000) {
        return
      }
      lastCheckRef.current = now

      try {
        // Add timeout to prevent hanging on slow networks
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Session check timeout')), 5000)
        })

        const sessionPromise = supabase.auth.getSession()
        const {
          data: { session },
        } = await Promise.race([sessionPromise, timeoutPromise])

        // Reset failure counter on success
        consecutiveFailuresRef.current = 0

        if (!session && !cancelled) {
          // Only navigate if we're not on an auth page already
          if (!location.pathname.startsWith('/login') && 
              !location.pathname.startsWith('/register') &&
              !location.pathname.startsWith('/reset-password')) {
            // Soft-guard: inform the user, then navigate using React Router
            showError('Your session has ended. Please log in again to continue.', {
              title: 'Session Ended',
            })

            navigate('/login', {
              replace: true,
              state: { from: location.pathname + location.search },
            })
          }
        }
      } catch (err) {
        // If the check fails, retry once after a delay (network issues, etc.)
        consecutiveFailuresRef.current++
        
        // Only treat as failure if we've failed multiple times
        // This prevents false positives from temporary network issues
        if (consecutiveFailuresRef.current >= 3 && !cancelled) {
          console.warn('Session guard check failed multiple times:', err)
          // Don't navigate on check failure - only navigate if session is actually null
          // This prevents false logouts during network issues
        } else if (!isRetry && !cancelled) {
          // Retry once after a short delay
          checkTimeoutRef.current = setTimeout(() => {
            runCheck(true)
          }, 1000)
        }
      }
    }

    // Initial check on mount (with delay to allow app to initialize)
    const initialTimeout = setTimeout(() => {
      if (!cancelled) {
        runCheck()
      }
    }, 500)

    // Re-check whenever the tab becomes visible again (with debounce)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Clear any pending checks
        if (checkTimeoutRef.current) {
          clearTimeout(checkTimeoutRef.current)
          checkTimeoutRef.current = null
        }
        
        // Debounce visibility checks - wait 1 second after tab becomes visible
        // This prevents false checks during quick tab switches
        checkTimeoutRef.current = setTimeout(() => {
          if (!cancelled) {
            runCheck()
          }
        }, 1000)
      } else {
        // Clear pending checks when tab becomes hidden
        if (checkTimeoutRef.current) {
          clearTimeout(checkTimeoutRef.current)
          checkTimeoutRef.current = null
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      cancelled = true
      clearTimeout(initialTimeout)
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current)
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [navigate, location, showError])
}


